/**
 * Steps Grid Component
 * 
 * A custom element that displays items in a grid with connecting lines,
 * useful for showing process steps, timelines, or sequential content.
 * 
 * The component dynamically calculates line lengths between image elements
 * to ensure proper visual connection regardless of image sizes or layout.
 */

// Unique name to avoid duplicate declaration when bundled with other modules (e.g. core.js)
const STEPS_GRID_MOBILE_PX = 750;

class StepsGrid extends HTMLElement {
  constructor() {
    super();
    this.resizeTimeout = null;
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.wrappers = this.querySelectorAll('.steps-grid__image-wrapper');
    
    if (this.wrappers.length < 2) return;

    // Wait for images to load before calculating
    this.waitForImages().then(() => {
      this.calculateLines();
    });

    // Recalculate on resize
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  /**
   * Wait for all images in the component to load
   * @returns {Promise}
   */
  waitForImages() {
    const images = this.querySelectorAll('img');
    
    if (images.length === 0) {
      return Promise.resolve();
    }

    const imagePromises = Array.from(images).map(img => {
      if (img.complete) {
        return Promise.resolve();
      }
      return new Promise(resolve => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    });

    return Promise.all(imagePromises);
  }

  /**
   * Handle window resize with debouncing
   */
  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.calculateLines();
    }, 100);
  }

  /**
   * Get the line buffer value from data attribute
   * @param {boolean} isMobile - Whether to get mobile or desktop buffer
   * @returns {number} Buffer in pixels
   */
  getBuffer(isMobile) {
    if (isMobile) {
      return parseInt(this.dataset.mobileLineBuffer, 10) || 0;
    }
    return parseInt(this.dataset.lineBuffer, 10) || 0;
  }

  /**
   * Calculate and set the line lengths between image wrappers
   */
  calculateLines() {
    const isMobile = window.innerWidth < STEPS_GRID_MOBILE_PX;
    const buffer = this.getBuffer(isMobile);
    // Total buffer applied to line (both ends)
    const totalBuffer = buffer * 2;

    this.wrappers.forEach((wrapper, index) => {
      // Skip the last item - no line needed after it
      if (index === this.wrappers.length - 1) {
        wrapper.style.removeProperty('--step-line-length');
        return;
      }

      const currentRect = wrapper.getBoundingClientRect();
      const nextWrapper = this.wrappers[index + 1];
      const nextRect = nextWrapper.getBoundingClientRect();

      let lineLength;

      if (isMobile) {
        // Vertical: from bottom of current image to top of next image
        lineLength = nextRect.top - currentRect.bottom;
      } else {
        // Horizontal: from right of current image to left of next image
        lineLength = nextRect.left - currentRect.right;
      }

      // Subtract buffer from both ends
      const adjustedLength = lineLength - totalBuffer;

      // Set the calculated length as a CSS variable
      wrapper.style.setProperty('--step-line-length', `${Math.max(0, adjustedLength)}px`);
    });
  }

  /**
   * Force recalculation (useful if content changes dynamically)
   */
  refresh() {
    this.wrappers = this.querySelectorAll('.steps-grid__image-wrapper');
    this.calculateLines();
  }
}

// Register the custom element
if (!customElements.get('steps-grid')) {
  customElements.define('steps-grid', StepsGrid);
}
